import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Settings, Check, X, Smartphone, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useOneSignalAuth } from '@/hooks/useOneSignalAuth';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';

interface OneSignalNotificationButtonProps {
  variant?: 'button' | 'card' | 'minimal';
  size?: 'sm' | 'default' | 'lg';
  showStatus?: boolean;
  customText?: {
    enable?: string;
    disable?: string;
    enabled?: string;
    disabled?: string;
  };
  onPermissionChange?: (enabled: boolean) => void;
}

export const OneSignalNotificationButton: React.FC<OneSignalNotificationButtonProps> = ({
  variant = 'button',
  size = 'default',
  showStatus = true,
  customText = {},
  onPermissionChange
}) => {
  const { user } = useAuth();
  const { 
    isLoading, 
    requestPermission, 
    getPermissionStatus, 
    getPlatformInfo, 
    getPermissionInstructions 
  } = useOneSignalAuth();
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const { t } = useTranslation();

  // Get platform-specific information
  const platformInfo = getPlatformInfo();
  const permissionInstructions = getPermissionInstructions();

  const text = {
    enable: customText.enable || permissionInstructions.actionText,
    disable: customText.disable || t('notifications_ui.disable_notifications'),
    enabled: customText.enabled || t('notifications_ui.enabled'),
    disabled: customText.disabled || t('notifications_ui.notifications_off'),
  };

  // Check permission status on mount
  useEffect(() => {
    const checkStatus = async () => {
      setIsCheckingStatus(true);
      try {
        const status = await getPermissionStatus();
        setHasPermission(status);
      } catch (error) {
        console.error('Error checking permission status:', error);
        setHasPermission(false);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    if (user) {
      checkStatus();
    }
  }, [user, getPermissionStatus]);

  const handleToggleNotifications = async () => {
    if (!user) {
      toast({
        title: t('notifications_ui.login_required'),
        description: t('notifications_ui.login_to_manage'),
        variant: "destructive"
      });
      return;
    }

    try {
      if (!hasPermission) {
        // Use platform-specific permission request
        const granted = await requestPermission();

        setHasPermission(granted);
        onPermissionChange?.(granted);

        if (granted) {
          toast({
            title: t('notifications_ui.notifications_enabled'),
            description: platformInfo.platform === 'ios' 
              ? t('notifications_ui.ios_success_message')
              : t('notifications_ui.android_success_message')
          });
        } else {
          // Show platform-specific guidance for denied permission
          const deniedMessage = platformInfo.platform === 'ios'
            ? t('notifications_ui.ios_denied_message')
            : t('notifications_ui.android_denied_message');
          
          toast({
            title: t('notifications_ui.permission_denied'),
            description: deniedMessage,
            variant: "destructive"
          });
        }
      } else {
        // Show platform-specific instructions for disabling
        const disableInstructions = platformInfo.platform === 'ios'
          ? t('notifications_ui.ios_disable_instructions')
          : t('notifications_ui.android_disable_instructions');
        
        toast({
          title: t('notifications_ui.disable_notifications'),
          description: disableInstructions
        });
      }
    } catch (error) {
      console.error('Error updating notification permission:', error);
      toast({
        title: t('common.error'),
        description: t('notifications_ui.error_updating'),
        variant: "destructive"
      });
    }
  };

  const renderIcon = () => {
    if (isCheckingStatus || isLoading) {
      return <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" />;
    }
    return hasPermission ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />;
  };

  const renderPlatformIcon = () => {
    if (platformInfo.platform === 'ios') {
      return <div className="w-3 h-3 bg-current rounded-sm" />; // iOS-style icon
    } else if (platformInfo.platform === 'android') {
      return <Smartphone className="w-3 h-3" />; // Android icon
    }
    return null;
  };

  const renderStatus = () => {
    if (isCheckingStatus) return null;
    
    return (
      <Badge 
        variant={hasPermission ? "default" : "secondary"}
        className={`${hasPermission ? 'bg-green-500 hover:bg-green-600 text-white' : 'bg-gray-500 text-white'}`}
      >
        {hasPermission ? (
          <>
            <Check className="w-3 h-3 mr-1" />
            {t('notifications_ui.on')}
          </>
        ) : (
          <>
            <X className="w-3 h-3 mr-1" />
            {t('notifications_ui.off')}
          </>
        )}
        {renderPlatformIcon() && <span className="ml-1">{renderPlatformIcon()}</span>}
      </Badge>
    );
  };

  const getPlatformSpecificContent = () => {
    if (platformInfo.platform === 'ios') {
      return {
        statusMessage: hasPermission 
          ? t('notifications_ui.ios_enabled_status')
          : t('notifications_ui.ios_enable_prompt'),
        enablePrompt: t('notifications_ui.ios_enable_prompt'),
        alertColor: hasPermission ? 'green' : 'blue'
      };
    } else if (platformInfo.platform === 'android') {
      return {
        statusMessage: hasPermission 
          ? t('notifications_ui.android_enabled_status')
          : t('notifications_ui.android_enable_prompt'),
        enablePrompt: t('notifications_ui.android_enable_prompt'),
        alertColor: hasPermission ? 'green' : 'orange'
      };
    } else {
      return {
        statusMessage: hasPermission 
          ? t('notifications_ui.enabled_status')
          : t('notifications_ui.enable_prompt'),
        enablePrompt: t('notifications_ui.enable_prompt'),
        alertColor: hasPermission ? 'green' : 'blue'
      };
    }
  };

  if (variant === 'minimal') {
    return (
      <Button
        variant="ghost"
        size={size}
        onClick={handleToggleNotifications}
        disabled={isLoading || isCheckingStatus}
        className="gap-2"
      >
        {renderIcon()}
        {showStatus && renderStatus()}
      </Button>
    );
  }

  if (variant === 'card') {
    const platformContent = getPlatformSpecificContent();
    
    return (
      <Card className="w-full max-w-md border border-border/20">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center justify-between gap-3 text-foreground">
            <span className="flex items-center gap-2 min-w-0">
              {renderIcon()}
              <span className="truncate">{permissionInstructions.title}</span>
            </span>
            {showStatus && <span className="shrink-0">{renderStatus()}</span>}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {permissionInstructions.description}
          </CardDescription>
          {/* Platform indicator */}
          {platformInfo.platform !== 'other' && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
              {renderPlatformIcon()}
              <span>{platformInfo.platform === 'ios' ? 'iOS' : 'Android'}</span>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {!isCheckingStatus && (
            <Alert className={`mb-4 ${
              platformContent.alertColor === 'green' 
                ? 'border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800' 
                : platformContent.alertColor === 'orange'
                ? 'border-orange-200 bg-orange-50 dark:bg-orange-950 dark:border-orange-800'
                : 'border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800'
            }`}>
              <Info className="h-4 w-4" />
              <AlertDescription className={`${
                platformContent.alertColor === 'green' 
                  ? 'text-green-800 dark:text-green-200' 
                  : platformContent.alertColor === 'orange'
                  ? 'text-orange-800 dark:text-orange-200'
                  : 'text-blue-800 dark:text-blue-200'
              }`}>
                {platformContent.statusMessage}
              </AlertDescription>
            </Alert>
          )}
          
          <Button
            onClick={handleToggleNotifications}
            disabled={isLoading || isCheckingStatus}
            className={`w-full text-white ${hasPermission ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-500 hover:bg-blue-600'}`}
            size="lg"
          >
            {isLoading ? (
              <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
            ) : (
              renderIcon()
            )}
            <span className="ml-2">
              {hasPermission ? text.enabled : text.enable}
            </span>
          </Button>

          {hasPermission && (
            <p className="text-xs text-muted-foreground mt-3 text-center">
              {platformInfo.platform === 'ios' 
                ? t('notifications_ui.ios_disable_instructions')
                : t('notifications_ui.android_disable_instructions')
              }
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  // Default button variant
  return (
    <Button
      variant={hasPermission ? "outline" : "default"}
      size={size}
      onClick={handleToggleNotifications}
      disabled={isLoading || isCheckingStatus}
      className={`gap-2 ${
        hasPermission 
          ? 'border-green-500 text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950' 
          : 'bg-blue-500 hover:bg-blue-600 text-white'
      }`}
    >
      {renderIcon()}
      <span>
        {hasPermission ? text.enabled : text.enable}
      </span>
      {showStatus && renderStatus()}
    </Button>
  );
};

// Pre-configured components for common use cases
export const NotificationCardWidget: React.FC = () => (
  <OneSignalNotificationButton variant="card" showStatus={true} />
);

export const NotificationToggleButton: React.FC = () => (
  <OneSignalNotificationButton variant="button" size="default" showStatus={true} />
);

export const NotificationMinimalIcon: React.FC = () => (
  <OneSignalNotificationButton variant="minimal" size="sm" showStatus={false} />
); 